import { useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BiographyEditor from '@/components/BiographyEditor';
import BiographyQualityPanel from '@/components/BiographyQualityPanel';
import { User, FileText, Shield } from 'lucide-react';
import { usePractitionerClients } from '@/hooks/usePractitionerClients';
import { useToast } from '@/hooks/use-toast';
import { parseFullName } from '@/utils/nameParser';
import type { PractitionerClient, CreateClientRequest } from '@/types/practitioner';

interface ClientFormProps {
  initialData?: PractitionerClient;
  onSubmit: (data: CreateClientRequest) => Promise<void>;
}

export const ClientForm = ({ initialData, onSubmit }: ClientFormProps) => {
  const { updateClient } = usePractitionerClients();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    notes: initialData?.notes || '',
    biography: initialData?.biography || '',
    content_exclusions: initialData?.content_exclusions || [],
    content_requirements: initialData?.content_requirements || [],
    session_directives: initialData?.session_directives || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingBiography, setIsSavingBiography] = useState(false);
  const biographySaveTimeout = useRef<NodeJS.Timeout | null>(null);

  const wordCount = formData.biography.trim().split(/\s+/).filter(word => word.length > 0).length;
  const maxCharacters = 2000; // Reasonable limit for client biographies

  // Email validation helper
  const isValidEmail = (email: string) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name.trim()) return;

    // Client-side validation
    if (formData.email && !isValidEmail(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined, 
        notes: formData.notes.trim() || undefined,
        biography: formData.biography.trim() || undefined,
        content_exclusions: formData.content_exclusions.length > 0 ? formData.content_exclusions : undefined,
        content_requirements: formData.content_requirements.length > 0 ? formData.content_requirements : undefined,
        session_directives: formData.session_directives.trim() || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBiographySave = useCallback(async () => {
    if (!initialData?.id) return; // Can only auto-save existing clients
    
    setIsSavingBiography(true);
    try {
      await updateClient(initialData.id, {
        biography: formData.biography.trim() || undefined,
      });
      
      toast({
        title: "Biography Saved",
        description: "Client biography has been saved automatically.",
      });
    } catch (error) {
      console.error('Error saving biography:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save biography. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingBiography(false);
    }
  }, [initialData?.id, formData.biography, updateClient, toast]);

  // Debounced auto-save for biography
  const debouncedBiographySave = useCallback(() => {
    if (biographySaveTimeout.current) {
      clearTimeout(biographySaveTimeout.current);
    }
    
    biographySaveTimeout.current = setTimeout(() => {
      if (initialData?.id && formData.biography.trim()) {
        handleBiographySave();
      }
    }, 2000); // Auto-save after 2 seconds of inactivity
  }, [handleBiographySave, initialData?.id, formData.biography]);

  const handleBiographyChange = (value: string) => {
    handleChange('biography', value);
    debouncedBiographySave();
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Basic Information
          </TabsTrigger>
          <TabsTrigger value="biography" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Biography
          </TabsTrigger>
          <TabsTrigger value="directives" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Content Directives
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="basic" className="space-y-4 mt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="e.g., Michele"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="e.g., Young"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="client@example.com"
              />
              {formData.email && !isValidEmail(formData.email) && (
                <p className="text-xs text-destructive">Please enter a valid email address</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional notes about this client..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="submit" disabled={isSubmitting || !formData.first_name.trim()}>
                {isSubmitting ? 'Saving...' : initialData ? 'Update Client' : 'Add Client'}
              </Button>
            </div>
          </form>
        </TabsContent>
        
        <TabsContent value="biography" className="mt-6 space-y-6">
          <BiographyEditor
            biography={formData.biography}
            setBiography={handleBiographyChange}
            saving={isSavingBiography}
            onSave={handleBiographySave}
            wordCount={wordCount}
            maxCharacters={maxCharacters}
          />
          
          {/* Biography Quality Panel */}
          {formData.biography.trim().length > 50 && (
            <BiographyQualityPanel
              biography={formData.biography}
              categoryId="biography"
              onBiographyChange={handleBiographyChange}
            />
          )}
          
          <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
            <h4 className="text-sm font-medium text-accent mb-2">Why Client Biography Matters</h4>
            <p className="text-sm text-muted-foreground">
              A detailed biography is the <strong>primary source</strong> for creating deeply personalized sessions. 
              Include their background, goals, preferences, challenges, and any specific details that would 
              enhance their meditation experience. This biography will be used as the main personalization 
              engine for all their sessions.
            </p>
          </div>
        </TabsContent>
        
        <TabsContent value="directives" className="mt-6 space-y-6">
          <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
            <h4 className="text-sm font-medium text-primary mb-2 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Clinical Content Control
            </h4>
            <p className="text-sm text-muted-foreground">
              As the practitioner, you have precise control over what the AI includes or excludes in sessions. 
              These directives ensure therapeutic appropriateness and client-specific considerations.
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="content_exclusions">Content Exclusions</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Topics, names, or content the AI must NOT mention (one per line)
              </p>
              <Textarea
                id="content_exclusions"
                value={formData.content_exclusions.join('\n')}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  content_exclusions: e.target.value.split('\n').filter(line => line.trim())
                }))}
                placeholder="ex-partner's name&#10;work stress&#10;family conflicts&#10;medical procedures"
                rows={4}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Examples: Names of people to avoid, sensitive topics, triggering words
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_requirements">Content Requirements</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Approaches, metaphors, or elements the AI MUST include (one per line)
              </p>
              <Textarea
                id="content_requirements"
                value={formData.content_requirements.join('\n')}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  content_requirements: e.target.value.split('\n').filter(line => line.trim())
                }))}
                placeholder="use ocean metaphors&#10;focus on self-compassion&#10;include breathing reminders&#10;gentle, nurturing language"
                rows={4}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Examples: Preferred metaphors, required therapeutic approaches, tone requirements
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="session_directives">Additional Session Instructions</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Free-form clinical guidance for this client's sessions
              </p>
              <Textarea
                id="session_directives"
                value={formData.session_directives}
                onChange={(e) => setFormData(prev => ({ ...prev, session_directives: e.target.value }))}
                placeholder="This client benefits from shorter sentences and more pauses. Focus on grounding techniques. Avoid rapid-fire instructions. Reference their love of gardening when possible for calming imagery."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Specific guidance based on your clinical assessment of this client
              </p>
            </div>

          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};